import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientTranslation } from '@/lib/core/i18n';
import { useApp } from '@/lib/core/providers/appProvider';
import { useCartStore } from '@/lib/core/stores';
import { mapCartErrorMessage } from '@/lib/core/shop/cartErrors';
import { Button } from '@/components/ui/button';
import { HeartToggle } from '@/components/shared/shop/wishlist/ui/HeartToggle';
import { ProductCardMedia, ProductCardInfo } from '@/components/shared/shop/product/ui/ProductCardMedia';
import { Toast, ToastErrorIcon } from '@/components/ui/toast';
import { ChooseSizeSheet } from '@/components/shared/shop/product/detail/sheets/ChooseSizeSheet';
import { ChooseQuantitySheet } from '@/components/shared/shop/product/detail/sheets/ChooseQuantitySheet';
import { getShopBagHref, type FavoriteShopProduct } from '@/services/platform/data/shop';
import type { ProductVariantDto } from '@/services/platform/api/model';

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDisplayPrice(favProduct: FavoriteShopProduct): number | undefined {
  const prices = (favProduct.product?.variants ?? [])
    .map(v => v.priceWithDiscount ?? v.price)
    .filter((p): p is number => p != null);
  return prices.length > 0 ? Math.min(...prices) : undefined;
}

/** Sold out (all variants out of stock) or no longer found in the catalog. */
export function isFavoriteProductUnavailable(favProduct: FavoriteShopProduct): boolean {
  if (!favProduct.product) return true;
  const variants = favProduct.product.variants ?? [];
  return variants.length > 0 && variants.every(v => (v.stock ?? 0) <= 0);
}

// ── Card ──────────────────────────────────────────────────────────────────────

function FavoritesProductCard({
  favProduct,
  locale,
  onRemove,
  onProductClick,
  onAddToBag,
}: {
  favProduct: FavoriteShopProduct;
  locale: string;
  onRemove: () => void;
  onProductClick: () => void;
  onAddToBag: () => void;
}) {
  const t = useClientTranslation();
  const { item, product } = favProduct;
  const unavailable = isFavoriteProductUnavailable(favProduct);
  const imageUrl = product?.images?.[0]?.src ?? item.itemImageUrl ?? undefined;
  const price = getDisplayPrice(favProduct);

  return (
    <div className="flex h-full flex-col items-start gap-2">
      <button type="button" className="w-full shrink-0" onClick={onProductClick}>
        <ProductCardMedia
          imageUrl={imageUrl}
          alt={product?.title ?? item.itemName ?? ''}
          soldOut={unavailable}
          imageBgClassName="bg-jncquoi-neutral-50"
        />
      </button>

      <div className="flex w-full items-start gap-2">
        <ProductCardInfo
          vendor={product?.vendor}
          title={product?.title ?? item.itemName}
          price={price}
          locale={locale}
        />
        <HeartToggle isFavorited onToggle={onRemove} size={24} className="shrink-0" />
      </div>

      <Button
        variant="secondary-outline"
        palette="dark"
        size="sm"
        disabled={unavailable}
        onClick={onAddToBag}
        className="disabled:border-jncquoi-neutral-300 mt-auto w-full"
      >
        {t('App.Shop.AddToBag')}
      </Button>
    </div>
  );
}

// ── Grid ──────────────────────────────────────────────────────────────────────

/**
 * Renders the Shop favorites as a 2-col product grid. "ADD TO BAG" reproduces the same
 * flow as the product detail screen (choose size/quantity → add to cart → confirmation),
 * rather than only deep-linking to the product page.
 */
export function FavoritesProductGrid({
  products,
  locale,
  onRemove,
  onProductClick,
}: {
  products: FavoriteShopProduct[];
  locale: string;
  onRemove: (itemGuid: string | undefined) => void;
  onProductClick: (publicGuid: string | undefined) => void;
}) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const { appSettings } = useApp();
  const addItem = useCartStore(state => state.addItem);
  const bagHref = getShopBagHref(appSettings);

  const [activeProduct, setActiveProduct] = useState<FavoriteShopProduct | undefined>(undefined);
  const [isSizeSheetOpen, setSizeSheetOpen] = useState(false);
  const [isQuantitySheetOpen, setQuantitySheetOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function handleAddToBagClick(favProduct: FavoriteShopProduct) {
    const product = favProduct.product;
    if (!product) return;
    setActiveProduct(favProduct);
    const isQuantityFlow = product.sections?.some(section => section.isQuantityFlow) ?? false;
    if (isQuantityFlow) {
      setQuantitySheetOpen(true);
    } else {
      setSizeSheetOpen(true);
    }
  }

  async function addVariantToBag(variantId: string | undefined, quantity: number) {
    if (!variantId) return;
    const result = await addItem(variantId, quantity);
    if (!result.success) {
      setErrorMessage(mapCartErrorMessage(t, result.error));
      return;
    }
    setSuccessMessage(t('App.Shop.ItemAddedToBag'));
  }

  async function handleSelectVariant(variant: ProductVariantDto) {
    setSizeSheetOpen(false);
    await addVariantToBag(variant.publicGuid, 1);
  }

  async function handleConfirmQuantity(quantity: number) {
    setQuantitySheetOpen(false);
    await addVariantToBag(activeProduct?.product?.variants?.[0]?.publicGuid, quantity);
  }

  return (
    <>
      <div className="grid grid-cols-2 items-stretch gap-x-2 gap-y-[43px]">
        {products.map(favProduct => (
          <FavoritesProductCard
            key={favProduct.item.publicGuid}
            favProduct={favProduct}
            locale={locale}
            onRemove={() => onRemove(favProduct.item.publicGuid)}
            onProductClick={() => onProductClick(favProduct.item.itemExternalId ?? undefined)}
            onAddToBag={() => handleAddToBagClick(favProduct)}
          />
        ))}
      </div>

      <ChooseSizeSheet
        open={isSizeSheetOpen}
        onOpenChange={setSizeSheetOpen}
        variants={activeProduct?.product?.variants ?? []}
        onSelect={handleSelectVariant}
      />

      <ChooseQuantitySheet
        open={isQuantitySheetOpen}
        onOpenChange={setQuantitySheetOpen}
        maxQuantity={activeProduct?.product?.variants?.[0]?.stock ?? 0}
        onConfirm={handleConfirmQuantity}
      />

      <Toast
        open={successMessage !== null}
        message={successMessage ?? ''}
        variant="light"
        actionLabel={t('App.Shop.ViewShoppingBag')}
        // `Toast` hides the action when `onAction` is absent, so an unmapped bag simply drops the
        // shortcut instead of offering a link to a guessed URL. The item is in the cart either way.
        onAction={bagHref ? () => navigate(bagHref) : undefined}
        onDismiss={() => setSuccessMessage(null)}
      />

      {/* Keeps the neutral `dark` background with the xmark glyph, as before. */}
      <Toast
        open={errorMessage !== null}
        message={errorMessage ?? ''}
        icon={ToastErrorIcon}
        onDismiss={() => setErrorMessage(null)}
      />
    </>
  );
}
